import React, { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import Badge from '../../common/Badge';
import FormControl from '../../common/FormControl';
import { capaArchiveService } from '../../../services/capaArchiveService';
import extractApiError from '../../../utils/apiError';
import NoDataFound from '../../common/NoDataFound';
import Skeleton from '../../common/Skeleton';
import { statusVariant } from '../../../utils/statusVariant';
import { usePermission } from '../../../contexts/PermissionContext';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';

const CompletedTasksPanel: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission('CAP_REMEDIATIONS', 'view');
    const { formatDate } = useEntitySettings();
    const [rows, setRows] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const load = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const { data, total } = await capaArchiveService.completedTasks({
                q: search || undefined,
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setRows(data);
            setTotalRecords(total);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load') });
        } finally { setLoading(false); }
    }, [search, page, pageSize, canView]);

    useEffect(() => { setPage(1); }, [search, pageSize]);
    useEffect(() => { load(); }, [load]);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to archived completed tasks." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search completed tasks…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_capa_completed_search"
                    />
                </div>
            </div>
            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Name</th>
                                        <th className="table-list-th">Issue</th>
                                        <th className="table-list-th">Doer</th>
                                        <th className="table-list-th">Closed</th>
                                        <th className="table-list-th table-list-cell--status">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'md', 'sm', 'sm', 'pill']} />}
                                    {!loading && rows.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="table-list-cell">
                                                <NoDataFound
                                                    title={search ? 'No Matching Tasks' : 'No Completed Tasks'}
                                                    message={search ? 'No completed tasks match your search.' : 'No tasks have been completed yet.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && rows.map((r, idx) => (
                                        <tr key={r.id} className="table-list-row">
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell"><span className="table-list-cell-title">{r.name}</span></td>
                                            <td className="table-list-cell">{r.issue_code} — {r.issue_name}</td>
                                            <td className="table-list-cell">{r.doer_name || '—'}</td>
                                            <td className="table-list-cell">{r.modified_on ? formatDate(r.modified_on) : '—'}</td>
                                            <td className="table-list-cell table-list-cell--status"><Badge variant={statusVariant(r.status)} size="sm">{r.status}</Badge></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={page} pageSize={pageSize} totalRecords={totalRecords} onPageChange={setPage} onPageSizeChange={setPageSize} pageSizeOptions={[10, 25, 50, 100]} recordLabel="tasks" />
                    </div>
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default CompletedTasksPanel;
